import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Plus,
    Filter,
    Loader2,
    FileText,
    Upload,
    ClipboardPaste,
    Search,
    StickyNote,
    Link as LinkIcon,
    ChevronDown,
    ChevronUp,
    ClipboardCheck,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import { useRevalidateOnFocus } from "../lib/useRevalidateOnFocus";
import { toast } from "sonner";
import ProjectHeader from "../components/ProjectHeader";
import SegmentedControl from "../components/SegmentedControl";
import AddExternalLinkDialog from "../components/AddExternalLinkDialog";
import SupervisorNotesList from "../components/SupervisorNotesList";
import SupervisorNotesImportDialog from "../components/SupervisorNotesImportDialog";
import AddSupervisorNoteDialog from "../components/AddSupervisorNoteDialog";
import ChangeDocumentsTimeline from "../components/ChangeDocumentsTimeline";
import ChangeDocumentUploadDialog from "../components/ChangeDocumentUploadDialog";
import EpisodeFilter from "../components/EpisodeFilter";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

/**
 * Iter-60: Dedicated Supervisor Notes screen.
 *
 * - Owner: import + filter + manage (delete) notes.
 * - Member: read + filter only.
 * Lives at /projects/:projectId/supervisor-notes.
 */
export default function SupervisorNotesPage() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const [project, setProject] = useState(null);
    const [scenes, setScenes] = useState([]);
    const [characters, setCharacters] = useState([]);
    const [sceneFilter, setSceneFilter] = useState("");
    const [characterFilter, setCharacterFilter] = useState("");
    // iter-80 PART 3 + 4 — search & team filter (mirrors Cast UX).
    const [searchQuery, setSearchQuery] = useState("");
    const [teamFilter, setTeamFilter] = useState("all"); // all | main | crowd
    const [importOpen, setImportOpen] = useState(false);
    const [uploadDocOpen, setUploadDocOpen] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    // Iter-98 — single text-note add dialog (parity with Scene/Cast detail).
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [docsRefreshKey, setDocsRefreshKey] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Iter-101 — episode awareness. `episodes` = every episode that
    // exists (sourced from /episodes); `episode` = active filter
    // (null → All). Passed as `?episode=X` into both the notes list
    // and the documents timeline.
    const [episodes, setEpisodes] = useState([]);
    const [episode, setEpisode] = useState(null);
    // Iter-131b — Active vs Archived segmented toggle. Backends now
    // hide archived rows by default, so flipping this re-fetches with
    // ?archived=true. Default stays on "active" so the operational
    // surface remains calm.
    const [archiveView, setArchiveView] = useState("active"); // "active" | "archived"
    // Iter-131b — Category filter pill rail (multi-select). Applies to
    // both supervisor_notes (server-side ?category=) and change_docs
    // (server-side ?kind=). The pill values map 1-to-1 to the category
    // taxonomy from PRD #2 / Iter-131a.
    const [activeCategories, setActiveCategories] = useState(() => new Set());

    // Iter-158.13.2.20f.5 #3 — Production destination presentation
    // switch. The Production Documents & Notes card links to
    // /beta/projects/:pid/supervisor-notes (apiBase `/beta/projects`);
    // every other mount (legacy `/projects`, H&M `/beta/hm-app/projects`)
    // keeps the historic "Notes" wording. Underlying models/routes/data
    // structures are untouched.
    const isProductionMount = apiBase === "/beta/projects";

    useEffect(() => {
        let aborted = false;
        (async () => {
            try {
                const [{ data: p }, { data: sc }, { data: ch }, { data: eps }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    api.get(`${apiBase}/${projectId}/scenes`),
                    api.get(`${apiBase}/${projectId}/characters`),
                    api
                        .get(`${apiBase}/${projectId}/episodes`)
                        .catch(() => ({ data: { episodes: [] } })),
                ]);
                if (aborted) return;
                // Iter-158.13.2.20f.3 #2 — Route 2 Hub GET returns
                // `{project, script}`; unwrap so downstream `project.role`
                // (used by the canManageNotes gate) resolves correctly on
                // the Production mount. Legacy `/api/projects/{pid}`
                // continues to return a flat project, so the guard is a
                // pass-through there.
                const flatProject = (p && typeof p === "object" && p.project && p.project.id)
                    ? p.project : p;
                setProject(flatProject);
                setScenes(sc || []);
                setCharacters(ch || []);
                setEpisodes(eps?.episodes || []);
            } catch (err) {
                if (!aborted) setError(formatApiError(err));
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [projectId]);

    // Iter-156E — project doc focus revalidation so out-of-band
    // changes (archive flip, script visibility, member role changes)
    // reach this surface without manual refresh.
    useRevalidateOnFocus(async () => {
        try {
            const { data } = await api.get(`${apiBase}/${projectId}`);
            // Iter-158.13.2.20f.3 #2 — same unwrap contract as the
            // initial load: Route 2 mount returns `{project, script}`.
            const flat = (data && typeof data === "object" && data.project && data.project.id)
                ? data.project : data;
            setProject(flat);
        } catch {
            /* swallow — initial load handles error state */
        }
    });

    const isOwner = project?.role === "owner";
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Iter-98 / Iter-156D — admins have owner-equivalent management
    // capability for notes/docs; Contributors can now add their own
    // scene/character notes too. Per-row Edit/Delete on the list is
    // additionally creator-or-admin scoped (backend enforces the
    // same).
    const canManageNotes =
        (isOwner ||
            project?.role === "admin" ||
            project?.role === "contributor") &&
        !isArchived;
    // Iter-156D — Bulk/admin-only actions (paste-import, document
    // upload, external link) remain Owner/Admin gated even though
    // Contributors can now add single text notes. Hiding the
    // dropdown items they don't have access to prevents the
    // click-to-403 dead end.
    const canAdminNotes =
        (isOwner || project?.role === "admin") && !isArchived;

    // iter-80 PART 4 — character.id → team lookup so SupervisorNotesList
    // can apply Main/Crowd filtering client-side without an extra fetch.
    const characterTeamMap = useMemo(() => {
        const m = {};
        for (const c of characters || []) {
            m[c.id] = c.team || "main";
        }
        return m;
    }, [characters]);

    // Iter-134 — Filter compression. The filter card was consuming
    // ~40% of vertical space on mobile. Default-collapsed on
    // narrow viewports, default-open on desktop. Locally
    // remembered per session so a user who taps it open stays
    // open until they collapse again or change page.
    const [filtersOpen, setFiltersOpen] = useState(() => {
        if (typeof window === "undefined") return true;
        return window.matchMedia("(min-width: 768px)").matches;
    });
    const filtersActive =
        sceneFilter || characterFilter || searchQuery || teamFilter !== "all" || episode || activeCategories.size > 0;

    return (
        <div
            data-testid="supervisor-notes-page"
            className="min-h-screen"
        >
            {/* Iter-158.13.2.20f.5 #3 — Production destination reads as
                "Documents" (the Documents & Notes card links here);
                every other mount keeps the historic "Notes" heading.
                Underlying models/routes are unchanged — the collection
                is still `supervisor_notes` and every API path is
                identical. Purely a presentation correction. */}
            <ProjectHeader
                projectName={project?.name}
                subtitle={isProductionMount ? "Documents" : "Notes"}
            />

            <main className="max-w-2xl mx-auto px-5 pt-5 pb-48">
                <header className="mb-5 flex items-start gap-3">
                    <div
                        data-testid="supervisor-notes-identity-icon"
                        className="h-10 w-10 rounded-lg bg-violet-500/15 border border-violet-500/30 text-violet-300 flex items-center justify-center shrink-0"
                    >
                        <ClipboardCheck size={18} />
                    </div>
                    <div className="min-w-0">
                        <h1
                            data-testid="supervisor-notes-page-heading"
                            className="font-display text-3xl font-bold text-white"
                        >
                            {isProductionMount ? "Documents" : "Notes"}
                        </h1>
                        <p className="text-sm text-neutral-400 mt-1.5 leading-relaxed">
                            {isProductionMount
                                ? "The Production's canonical documents and notes. Shared across every department that has read access."
                                : "Notes are reference only. They do not change scenes or characters."}
                        </p>
                    </div>
                </header>

                {error ? (
                    <p
                        data-testid="supervisor-notes-page-error"
                        className="text-sm text-red-400 mb-3"
                    >
                        {error}
                    </p>
                ) : null}

                {/* iter-76 PART 3 — single "Add Note" mental
                    entry point. The two prior buttons (Import notes,
                    Upload document) felt like the same action and
                    confused users. Now there's ONE owner-only CTA
                    that opens a tiny chooser sheet — Paste notes
                    (text-batch import) or Upload document (PDF /
                    image / DOCX). Underlying dialogs are unchanged. */}
                {canManageNotes ? (
                    <div className="mb-5">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    type="button"
                                    data-testid="supervisor-notes-add-trigger"
                                    className="w-full sm:w-auto min-tap font-semibold"
                                >
                                    <Plus size={14} className="mr-1.5" />
                                    {/* Iter-158.13.2.20f.5 #3 — the Production
                                        Documents destination shortens the
                                        trigger to "Add"; the specific
                                        choices (Add text note / Upload
                                        document / Paste notes / Add external
                                        link) remain in the dropdown below. */}
                                    {isProductionMount ? "Add" : "Add note"}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="start"
                                className="w-72"
                                data-testid="supervisor-notes-add-menu"
                            >
                                <DropdownMenuItem
                                    data-testid="supervisor-notes-add-text"
                                    onSelect={() => setAddNoteOpen(true)}
                                    className="cursor-pointer flex-col items-start gap-0.5 py-2"
                                >
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <StickyNote size={14} />
                                        Add text note
                                    </div>
                                    <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                        A single note. Optionally link to a
                                        scene or character.
                                    </div>
                                </DropdownMenuItem>
                                {canAdminNotes ? (
                                <DropdownMenuItem
                                    data-testid="supervisor-notes-add-paste"
                                    onSelect={() => setImportOpen(true)}
                                    className="cursor-pointer flex-col items-start gap-0.5 py-2"
                                >
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <ClipboardPaste size={14} />
                                        Paste notes
                                    </div>
                                    <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                        Pasted or typed notes — auto-mapped
                                        to scenes &amp; characters.
                                    </div>
                                </DropdownMenuItem>
                                ) : null}
                                {/* Iter-156G — Upload + Link are now
                                    write-capable (Owner / Admin /
                                    Contributor). Bulk paste import
                                    remains Admin-only above. */}
                                <DropdownMenuItem
                                    data-testid="supervisor-notes-add-upload"
                                    onSelect={() => setUploadDocOpen(true)}
                                    className="cursor-pointer flex-col items-start gap-0.5 py-2"
                                >
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <Upload size={14} />
                                        Upload document
                                    </div>
                                    <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                        File upload (PDF, DOCX, image) —
                                        stored as reference only.
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    data-testid="supervisor-notes-add-link"
                                    onSelect={() => setLinkDialogOpen(true)}
                                    className="cursor-pointer flex-col items-start gap-0.5 py-2"
                                >
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <LinkIcon size={14} />
                                        Add external link
                                    </div>
                                    <div className="text-[11px] text-neutral-400 ml-6 leading-snug">
                                        Google Sheets, Numbers, Excel Online —
                                        stays a reference, never embedded.
                                    </div>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ) : null}

                <section
                    data-testid="supervisor-notes-filters"
                    className="rounded-xl border border-white/10 bg-card p-4 mb-5"
                >
                    {/* Iter-134 — Collapsible header. Surfaces active
                        filter count as a calm pill so the user
                        knows the filters are working even when
                        collapsed. */}
                    <button
                        type="button"
                        data-testid="supervisor-notes-filters-toggle"
                        onClick={() => setFiltersOpen((v) => !v)}
                        className="tap w-full flex items-center justify-between gap-2"
                        aria-expanded={filtersOpen}
                    >
                        <h2 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 flex items-center gap-2">
                            <Filter size={11} /> Filter
                            {filtersActive ? (
                                <span
                                    data-testid="supervisor-notes-filters-active-pill"
                                    className="ml-1 text-[9px] tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200"
                                >
                                    active
                                </span>
                            ) : null}
                        </h2>
                        {filtersOpen ? (
                            <ChevronUp size={14} className="text-neutral-500" />
                        ) : (
                            <ChevronDown size={14} className="text-neutral-500" />
                        )}
                    </button>
                    {filtersOpen ? (
                    <div className="mt-3">
                    {/* Iter-131b — Active / Archived segmented toggle.
                        Sits at the very top of the filter card so the
                        archive section feels like a different mode of
                        the same view (not a different page). */}
                    <div
                        data-testid="supervisor-notes-archive-toggle"
                        className="inline-flex rounded-full border border-white/10 bg-secondary/50 p-0.5 mb-3 self-start"
                        role="tablist"
                    >
                        <button
                            type="button"
                            role="tab"
                            data-testid="supervisor-notes-archive-active"
                            aria-selected={archiveView === "active"}
                            onClick={() => setArchiveView("active")}
                            className={`tap min-h-9 px-3 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                                archiveView === "active"
                                    ? "bg-primary text-primary-foreground"
                                    : "text-neutral-400 hover:text-white"
                            }`}
                        >
                            Active
                        </button>
                        <button
                            type="button"
                            role="tab"
                            data-testid="supervisor-notes-archive-archived"
                            aria-selected={archiveView === "archived"}
                            onClick={() => setArchiveView("archived")}
                            className={`tap min-h-9 px-3 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors ${
                                archiveView === "archived"
                                    ? "bg-amber-500/85 text-amber-950"
                                    : "text-neutral-400 hover:text-white"
                            }`}
                        >
                            Archived
                        </button>
                    </div>
                    {/* Iter-131b — Category pill rail. Multi-select. */}
                    <CategoryFilterPills
                        active={activeCategories}
                        onToggle={(value) => {
                            setActiveCategories((prev) => {
                                const next = new Set(prev);
                                if (next.has(value)) next.delete(value);
                                else next.add(value);
                                return next;
                            });
                        }}
                        onClear={() => setActiveCategories(new Set())}
                    />
                    {/* iter-80 PART 3 — Search bar above the existing
                        scene/character selects. Searches note text, the
                        scene reference (number/heading) and the character
                        reference (name) client-side. */}
                    <div className="relative mb-3">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
                        />
                        <Input
                            data-testid="supervisor-notes-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search notes"
                            inputMode="search"
                            autoComplete="off"
                            className="h-11 pl-9 pr-9 bg-secondary border-transparent text-base"
                        />
                        {searchQuery ? (
                            <button
                                type="button"
                                data-testid="supervisor-notes-search-clear"
                                onClick={() => setSearchQuery("")}
                                aria-label="Clear search"
                                className="tap absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-white hover:bg-white/5"
                            >
                                ×
                            </button>
                        ) : null}
                    </div>
                    {/* iter-101 — episode filter. Hidden for pure-film
                        projects (no episodes). Slots in above the
                        scene/character selects so the user narrows
                        from episode → scene → character. */}
                    <EpisodeFilter
                        testid="supervisor-notes-episode-filter"
                        episodes={episodes}
                        value={episode}
                        onChange={(v) => {
                            setEpisode(v);
                            // Clear scene/character filters that belong
                            // to a different episode so the dropdowns
                            // don't silently narrow the results past
                            // what the user asked for.
                            if (v) {
                                if (
                                    sceneFilter &&
                                    !scenes.some((s) => s.id === sceneFilter && String(s.episode || "") === String(v))
                                ) {
                                    setSceneFilter("");
                                }
                            }
                        }}
                        className="mb-0"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="text-xs text-neutral-300">
                            <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                Scene
                            </span>
                            <select
                                data-testid="supervisor-notes-filter-scene"
                                value={sceneFilter}
                                onChange={(e) =>
                                    setSceneFilter(e.target.value)
                                }
                                className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-white"
                            >
                                <option value="">All scenes</option>
                                {scenes.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.episode
                                            ? `EP ${s.episode} · `
                                            : ""}
                                        Sc {s.scene_number || "—"} —{" "}
                                        {s.heading || ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="text-xs text-neutral-300">
                            <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1">
                                Character
                            </span>
                            <select
                                data-testid="supervisor-notes-filter-character"
                                value={characterFilter}
                                onChange={(e) =>
                                    setCharacterFilter(e.target.value)
                                }
                                className="w-full h-11 bg-secondary border border-white/10 rounded-md px-3 text-sm text-white"
                            >
                                <option value="">All characters</option>
                                {characters.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {/* iter-80 PART 4 — Team filter (All / Main / Crowd)
                        mirrors CastHub team filter so behaviour is
                        consistent across the app. */}
                    <div className="mt-3">
                        <span className="block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5">
                            Team
                        </span>
                        <SegmentedControl
                            testid="supervisor-notes-filter-team"
                            variant="pills"
                            value={teamFilter}
                            onChange={setTeamFilter}
                            options={[
                                { value: "all", label: "All" },
                                { value: "main", label: "Main Team" },
                                { value: "crowd", label: "Crowd" },
                            ]}
                        />
                    </div>
                    {(sceneFilter || characterFilter || searchQuery || teamFilter !== "all") ? (
                        <button
                            type="button"
                            data-testid="supervisor-notes-filter-clear"
                            onClick={() => {
                                setSceneFilter("");
                                setCharacterFilter("");
                                setSearchQuery("");
                                setTeamFilter("all");
                            }}
                            className="mt-3 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-white"
                        >
                            Clear filters
                        </button>
                    ) : null}
                    </div>
                    ) : null}
                </section>

                {loading ? (
                    <div className="h-24 rounded-xl border border-white/10 bg-card/40 animate-pulse flex items-center justify-center text-neutral-500 text-sm gap-2">
                        <Loader2 size={14} className="animate-spin" /> Loading…
                    </div>
                ) : (
                    <>
                        <h2 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2 mt-2">
                            Notes
                        </h2>
                        <SupervisorNotesList
                            key={`${refreshKey}-${sceneFilter}-${characterFilter}-${episode || "all"}-${archiveView}-${[...activeCategories].sort().join(",")}`}
                            projectId={projectId}
                            sceneId={sceneFilter || undefined}
                            characterId={characterFilter || undefined}
                            episode={episode}
                            isOwner={canAdminNotes}
                            searchQuery={searchQuery}
                            teamFilter={teamFilter}
                            characterTeamMap={characterTeamMap}
                            archived={archiveView === "archived" ? "true" : "false"}
                            categories={
                                activeCategories.size > 0
                                    ? [...activeCategories].join(",")
                                    : null
                            }
                            emptyHint={
                                archiveView === "archived"
                                    ? "No archived notes."
                                    : sceneFilter || characterFilter || searchQuery || teamFilter !== "all" || episode || activeCategories.size > 0
                                    ? "No notes match this filter."
                                    : "No notes yet. Import a batch or upload a document above."
                            }
                            testidPrefix="supervisor-notes-page"
                            onChange={() => setRefreshKey((v) => v + 1)}
                        />

                        <h2 className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 mb-2 mt-6 flex items-center gap-2">
                            <FileText size={11} /> Documents
                        </h2>
                        <ChangeDocumentsTimeline
                            key={`docs-${docsRefreshKey}-${sceneFilter}-${characterFilter}-${episode || "all"}-${archiveView}-${[...activeCategories].sort().join(",")}`}
                            projectId={projectId}
                            sceneId={sceneFilter || undefined}
                            characterId={characterFilter || undefined}
                            episode={episode}
                            archived={archiveView === "archived" ? "true" : "false"}
                            kinds={
                                activeCategories.size > 0
                                    ? [...activeCategories].join(",")
                                    : null
                            }
                            isOwner={canAdminNotes}
                            testidPrefix="change-documents-page"
                            emptyHint={
                                archiveView === "archived"
                                    ? "No archived documents."
                                    : "No uploaded documents yet."
                            }
                            onChange={() => setDocsRefreshKey((v) => v + 1)}
                        />
                    </>
                )}

                <SupervisorNotesImportDialog
                    projectId={projectId}
                    open={importOpen}
                    onOpenChange={setImportOpen}
                    onImported={() => setRefreshKey((v) => v + 1)}
                />
                <ChangeDocumentUploadDialog
                    projectId={projectId}
                    open={uploadDocOpen}
                    onOpenChange={setUploadDocOpen}
                    scenes={scenes}
                    characters={characters}
                    onUploaded={() => setDocsRefreshKey((v) => v + 1)}
                />
                <AddExternalLinkDialog
                    projectId={projectId}
                    open={linkDialogOpen}
                    onOpenChange={setLinkDialogOpen}
                    onCreated={() => setDocsRefreshKey((v) => v + 1)}
                />
                <AddSupervisorNoteDialog
                    open={addNoteOpen}
                    onOpenChange={setAddNoteOpen}
                    scenes={scenes}
                    characters={characters}
                    presetSceneId={sceneFilter || null}
                    presetCharacterId={characterFilter || null}
                    onCreate={async (payload) => {
                        try {
                            await api.post(
                                `${apiBase}/${projectId}/supervisor-notes`,
                                payload,
                            );
                            toast.success("Note added");
                            setRefreshKey((v) => v + 1);
                        } catch (err) {
                            toast.error(formatApiError(err));
                            throw err;
                        }
                    }}
                />
            </main>
        </div>
    );
}

/**
 * Iter-131b — Calm, scannable category pill rail. Multi-select. Each
 * pill is a button toggle; an additional "Clear" pill appears once any
 * are active so the user can return to a wide view in one tap.
 *
 * The taxonomy mirrors the kind values registered in CHANGE_DOC_KIND_VALUES
 * (server-side). Supervisor notes use the SAME strings as a free-text
 * `category` field, so a multi-select pill rail applies to both
 * collections cleanly.
 */
const CATEGORY_PILLS = [
    { value: "call_sheet", label: "Call sheets" },
    { value: "schedule", label: "Schedules" },
    { value: "email", label: "Emails" },
    { value: "reference", label: "References" },
    { value: "budget", label: "Budgets" },
    { value: "location", label: "Locations" },
    { value: "continuity", label: "Continuity" },
    { value: "research", label: "Research" },
    { value: "link", label: "Links" },
];

function CategoryFilterPills({ active, onToggle, onClear }) {
    return (
        <div
            data-testid="supervisor-notes-category-pills"
            className="flex flex-wrap gap-1.5 mb-3"
        >
            {CATEGORY_PILLS.map((p) => {
                const on = active.has(p.value);
                return (
                    <button
                        key={p.value}
                        type="button"
                        data-testid={`supervisor-notes-category-${p.value}`}
                        aria-pressed={on}
                        onClick={() => onToggle(p.value)}
                        className={`tap min-h-7 px-2.5 rounded-full text-[11px] font-medium transition-colors border ${
                            on
                                ? "bg-violet-500/25 border-violet-400/40 text-violet-100"
                                : "bg-secondary/40 border-white/10 text-neutral-300 hover:bg-white/5 hover:text-white"
                        }`}
                    >
                        {p.label}
                    </button>
                );
            })}
            {active.size > 0 ? (
                <button
                    type="button"
                    data-testid="supervisor-notes-category-clear"
                    onClick={onClear}
                    className="tap min-h-7 px-2.5 rounded-full text-[11px] font-medium border border-white/10 text-neutral-400 hover:text-white hover:bg-white/5"
                >
                    Clear
                </button>
            ) : null}
        </div>
    );
}
